export const SETTINGS_PLUGIN_PANEL_IDS = Object.freeze([
  'users',
  'sessions',
  'audit',
  'usage',
  'appearance',
  'archive'
]);

export const SETTINGS_PLUGIN_APPEARANCE_SCOPE_POLICIES = Object.freeze([
  'settings-only',
  'runtime-eligible'
]);

export const SETTINGS_PLUGIN_APP_CONTRIBUTION_SCOPES = Object.freeze([
  'personal',
  'tenant'
]);

export const SETTINGS_PLUGIN_APP_CONTRIBUTION_ID_PATTERN = /^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/u;
export const SETTINGS_PLUGIN_PROVIDER_ID_PATTERN = /^[a-z0-9][a-z0-9._-]*$/u;

export const DEFAULT_SETTINGS_PLUGIN_POLICY = Object.freeze({
  visiblePanelsByRole: Object.freeze({
    default: Object.freeze(['appearance']),
    admin: Object.freeze(['users', 'sessions', 'audit', 'usage', 'appearance', 'archive'])
  }),
  appearanceScopePolicy: Object.freeze({
    default: 'runtime-eligible',
    admin: 'runtime-eligible'
  }),
  appContribution: null
});

function normalizeRequiredCapability(value, errors, fieldPath) {
  if (value == null) return '';
  const normalized = String(value || '').trim();
  if (!normalized) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be non-empty when provided.`);
    return '';
  }
  if (normalized.length > 160 || !/^[A-Za-z0-9][A-Za-z0-9.*:_-]*$/u.test(normalized)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be a valid capability key.`);
    return '';
  }
  return normalized;
}

function normalizeContributionDescriptors(value, errors, fieldPath, seenIds, maxItems) {
  if (value == null) return [];
  if (!Array.isArray(value)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be an array.`);
    return [];
  }

  if (value.length > maxItems) {
    errors.push(`Invalid app manifest field: ${fieldPath} cannot contain more than ${maxItems} entries.`);
  }
  const descriptors = [];
  value.slice(0, maxItems).forEach((item, index) => {
    const itemPath = `${fieldPath}[${index}]`;
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      errors.push(`Invalid app manifest field: ${itemPath} must be an object.`);
      return;
    }

    const rawId = String(item.id || '').trim();
    const id = rawId.toLowerCase();
    const label = String(item.label || '').trim();
    const order = Number(item.order);
    const scope = String(item.scope || '').trim().toLowerCase();

    let valid = true;
    if (SETTINGS_PLUGIN_PANEL_IDS.includes(id)) {
      errors.push(`Invalid app manifest field: ${itemPath}.id cannot collide with a core Settings panel id.`);
      valid = false;
    } else if (!rawId || rawId !== id || !SETTINGS_PLUGIN_APP_CONTRIBUTION_ID_PATTERN.test(id)) {
      errors.push(`Invalid app manifest field: ${itemPath}.id must be a lowercase namespaced id such as app.profile.`);
      valid = false;
    } else if (seenIds.has(id)) {
      errors.push(`Invalid app manifest field: ${itemPath}.id must be unique across Settings contributions.`);
      valid = false;
    }
    if (!label || label.length > 80) {
      errors.push(`Invalid app manifest field: ${itemPath}.label must contain 1-80 characters.`);
      valid = false;
    }
    if (!Number.isInteger(order) || order < 0 || order > 10000) {
      errors.push(`Invalid app manifest field: ${itemPath}.order must be an integer from 0 to 10000.`);
      valid = false;
    }
    if (!SETTINGS_PLUGIN_APP_CONTRIBUTION_SCOPES.includes(scope)) {
      errors.push(`Invalid app manifest field: ${itemPath}.scope must be one of ${SETTINGS_PLUGIN_APP_CONTRIBUTION_SCOPES.join('|')}.`);
      valid = false;
    }

    const requiredCapability = normalizeRequiredCapability(
      item.requiredCapability,
      errors,
      `${itemPath}.requiredCapability`
    );
    if (item.requiredCapability != null && !requiredCapability) valid = false;
    if (!valid) return;

    seenIds.add(id);
    descriptors.push({
      id,
      label,
      order,
      scope,
      ...(requiredCapability ? { requiredCapability } : {})
    });
  });

  return descriptors.sort((left, right) => (
    left.order - right.order || left.id.localeCompare(right.id)
  ));
}

function normalizeAppContribution(value, errors) {
  if (value == null) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution must be an object.');
    return null;
  }

  const rawProviderPluginId = String(value.providerPluginId || '').trim();
  const providerPluginId = rawProviderPluginId.toLowerCase();
  let providerValid = true;
  if (
    !rawProviderPluginId
    || rawProviderPluginId !== providerPluginId
    || rawProviderPluginId.length > 120
    || !SETTINGS_PLUGIN_PROVIDER_ID_PATTERN.test(providerPluginId)
  ) {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution.providerPluginId must be a lowercase plugin id.');
    providerValid = false;
  }
  if (providerPluginId === 'platform.settings') {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution.providerPluginId cannot be platform.settings.');
    providerValid = false;
  }

  const seenIds = new Set();
  const panels = normalizeContributionDescriptors(
    value.panels,
    errors,
    'settingsPlugin.appContribution.panels',
    seenIds,
    24
  );
  const appearanceSections = normalizeContributionDescriptors(
    value.appearanceSections,
    errors,
    'settingsPlugin.appContribution.appearanceSections',
    seenIds,
    12
  );
  if (panels.length === 0 && appearanceSections.length === 0) {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution must declare at least one panel or Appearance section.');
  }

  const bridgeCoreThemePreference = value.bridgeCoreThemePreference === true;
  if (value.bridgeCoreThemePreference != null && typeof value.bridgeCoreThemePreference !== 'boolean') {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution.bridgeCoreThemePreference must be a boolean.');
  }
  if (bridgeCoreThemePreference && appearanceSections.length === 0) {
    errors.push('Invalid app manifest field: settingsPlugin.appContribution.bridgeCoreThemePreference requires an Appearance section.');
  }

  if (!providerValid) return null;
  return {
    providerPluginId,
    panels,
    appearanceSections,
    bridgeCoreThemePreference
  };
}

function normalizeVisiblePanels(value, fallback, errors, fieldPath) {
  if (value == null) return [...fallback];
  if (!Array.isArray(value)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be an array.`);
    return [...fallback];
  }
  const out = [];
  const seen = new Set();
  value.forEach((item) => {
    const panelId = String(item || '').trim().toLowerCase();
    if (!panelId) return;
    if (!SETTINGS_PLUGIN_PANEL_IDS.includes(panelId)) {
      errors.push(
        `Invalid app manifest field: ${fieldPath} entries must be one of ${SETTINGS_PLUGIN_PANEL_IDS.join('|')}.`
      );
      return;
    }
    if (seen.has(panelId)) return;
    seen.add(panelId);
    out.push(panelId);
  });
  return out.length > 0 ? out : [...fallback];
}

function normalizeDefaultVisiblePanels(value, fallback, errors) {
  const normalized = normalizeVisiblePanels(
    value,
    fallback,
    errors,
    'settingsPlugin.visiblePanelsByRole.default'
  ).filter((panelId) => {
    if (panelId === 'appearance') return true;
    errors.push('Invalid app manifest field: settingsPlugin.visiblePanelsByRole.default can only include appearance.');
    return false;
  });
  return normalized.length > 0 ? normalized : [...fallback];
}

function normalizeAppearanceScopePolicy(value, fallback, errors, fieldPath) {
  if (value == null) return fallback;
  const normalized = String(value || '').trim().toLowerCase();
  if (SETTINGS_PLUGIN_APPEARANCE_SCOPE_POLICIES.includes(normalized)) return normalized;
  errors.push(
    `Invalid app manifest field: ${fieldPath} must be one of ${SETTINGS_PLUGIN_APPEARANCE_SCOPE_POLICIES.join('|')}.`
  );
  return fallback;
}

export function normalizeSettingsPluginPolicy(source, errors = []) {
  const raw = source && typeof source === 'object' ? source : {};
  if (source != null && (typeof source !== 'object' || Array.isArray(source))) {
    errors.push('Invalid app manifest field: settingsPlugin must be an object.');
  }
  const rawVisiblePanels = raw.visiblePanelsByRole && typeof raw.visiblePanelsByRole === 'object'
    ? raw.visiblePanelsByRole
    : {};
  if (
    Object.prototype.hasOwnProperty.call(raw, 'visiblePanelsByRole')
    && (!raw.visiblePanelsByRole || typeof raw.visiblePanelsByRole !== 'object' || Array.isArray(raw.visiblePanelsByRole))
  ) {
    errors.push('Invalid app manifest field: settingsPlugin.visiblePanelsByRole must be an object.');
  }
  const rawAppearanceScopePolicy = raw.appearanceScopePolicy && typeof raw.appearanceScopePolicy === 'object'
    ? raw.appearanceScopePolicy
    : {};
  if (
    Object.prototype.hasOwnProperty.call(raw, 'appearanceScopePolicy')
    && (!raw.appearanceScopePolicy || typeof raw.appearanceScopePolicy !== 'object' || Array.isArray(raw.appearanceScopePolicy))
  ) {
    errors.push('Invalid app manifest field: settingsPlugin.appearanceScopePolicy must be an object.');
  }

  return {
    visiblePanelsByRole: {
      default: normalizeDefaultVisiblePanels(
        rawVisiblePanels.default,
        DEFAULT_SETTINGS_PLUGIN_POLICY.visiblePanelsByRole.default,
        errors
      ),
      admin: normalizeVisiblePanels(
        rawVisiblePanels.admin,
        DEFAULT_SETTINGS_PLUGIN_POLICY.visiblePanelsByRole.admin,
        errors,
        'settingsPlugin.visiblePanelsByRole.admin'
      )
    },
    appearanceScopePolicy: {
      default: normalizeAppearanceScopePolicy(
        rawAppearanceScopePolicy.default,
        DEFAULT_SETTINGS_PLUGIN_POLICY.appearanceScopePolicy.default,
        errors,
        'settingsPlugin.appearanceScopePolicy.default'
      ),
      admin: normalizeAppearanceScopePolicy(
        rawAppearanceScopePolicy.admin,
        DEFAULT_SETTINGS_PLUGIN_POLICY.appearanceScopePolicy.admin,
        errors,
        'settingsPlugin.appearanceScopePolicy.admin'
      )
    },
    appContribution: normalizeAppContribution(raw.appContribution, errors)
  };
}
